import {
  addSpend,
  AmountField,
  categories,
  convert,
  countryName,
  currencyName,
  currencySymbol,
  Dialog,
  formatMoney,
  formatRange,
  getToday,
  InvalidInput,
  NotFound,
  removeRate,
  removeSpend,
  setRate,
  spendBillUrl,
  toIso,
  uploadSpendBill,
  type Category,
  type Rate,
  type TodayScreen,
} from '@trippath/shared';
import { addDays, format } from 'date-fns';
import { enGB, et } from 'date-fns/locale';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { problemKey } from '../../lib/format';
import { drop, keep, send, waiting, type Waiting } from './outbox';
import { TodayWeather } from './TodayWeather';
import { useLive } from '../../app/live';
import { OfflineToday } from './OfflineToday';
import { useReadOnly } from '../../app/readOnly';

/** For what fails on purpose quietly: the page shows what it had. */
const ignore = () => undefined;

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; screen: TodayScreen };

/** `Sat 14 Nov`, in the UI language. */
function weekday(iso: string, language: string): string {
  return format(new Date(`${iso}T00:00`), 'EEE d MMM', {
    locale: language === 'et' ? et : enGB,
  });
}

/** The day before or after `iso`. */
function shift(iso: string, by: number): string {
  return toIso(addDays(new Date(`${iso}T00:00`), by));
}

/** `1 € = 30,500 ₫`: what one unit of the trip's money buys. */
function rateText(rate: Rate, trip: string, language: string): string {
  const amount = new Intl.NumberFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    maximumFractionDigits: 4,
  }).format(rate.perTrip / 1e9);
  return `1 ${currencySymbol(trip, language)} = ${amount} ${currencySymbol(rate.currency, language)}`;
}

/**
 * The phone on the road (the Today mockup): what is left of the day's money, a spend logged in
 * the money it was paid in with a photo of the bill, the day's spends, and the way on.
 */
export function Today() {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [day, setDay] = useState(() => toIso(new Date()));
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [version, setVersion] = useState(0);
  const [waits, setWaits] = useState<Waiting[]>([]);
  const [online, setOnline] = useState(() => navigator.onLine);
  const reload = () => {
    setVersion((was) => was + 1);
  };
  const recount = () => {
    waiting(id).then(setWaits, ignore);
  };

  // What waits on the phone is sent when the page opens and whenever the connection is back.
  useEffect(() => {
    let live = true;
    const flush = () => {
      send(id)
        .then((sent) => {
          if (live && sent > 0) setVersion((was) => was + 1);
          return waiting(id);
        })
        .then((list) => {
          if (live) setWaits(list);
        }, ignore);
    };
    const up = () => {
      setOnline(true);
      flush();
    };
    const down = () => {
      setOnline(false);
    };
    flush();
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      live = false;
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, [id, changed]);

  useEffect(() => {
    let live = true;
    getToday(id, day).then(
      (screen) => {
        if (live) setState({ kind: 'ready', screen });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, day, version, changed]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p role="alert">{t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}</p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { screen } = state;
  const money = (amount: { minor: number; currency: string }) =>
    formatMoney(amount, lang, true);
  const allowed = screen.allowance?.minor ?? 0;
  const left = allowed - screen.spent.minor;
  const spentShare = allowed > 0 ? Math.min(screen.spent.minor / allowed, 1) : 0;
  const pending = waits.filter((w) => w.refused === null).length;

  return (
    <div className="page today">
      <section className="today-head" aria-labelledby="today-title">
        <div className="today-days">
          <button
            type="button"
            className="button button-on-bar"
            aria-label={t('today.before')}
            onClick={() => {
              setDay(shift(day, -1));
            }}
          >
            ‹
          </button>
          <p className="today-date">
            {weekday(screen.day, lang)}
            {screen.tripDay &&
              ` · ${t('today.tripDay', { day: screen.tripDay.day, of: screen.tripDay.of })}`}
          </p>
          <button
            type="button"
            className="button button-on-bar"
            aria-label={t('today.after')}
            onClick={() => {
              setDay(shift(day, 1));
            }}
          >
            ›
          </button>
        </div>
        {(!online || pending > 0) && (
          <p className="today-pill" role="status">
            {online ? t('today.waiting', { count: pending }) : t('today.offline')}
          </p>
        )}
        <h1 id="today-title">
          {screen.here
            ? t('today.here', {
                city: screen.here.city,
                night: screen.here.night,
                nights: screen.here.nights,
              })
            : screen.startsIn === null
              ? t('today.ended')
              : t('today.startsIn', { count: screen.startsIn })}
        </h1>
        {screen.allowance && (
          <div className="today-left">
            <div>
              <p className="today-label">{t('today.left')}</p>
              <p className={`today-amount${left < 0 ? ' over' : ''}`}>
                {money({ minor: left, currency: screen.currency })}
              </p>
            </div>
            <div className="today-side">
              <p>
                {t('today.spentOf', {
                  spent: money(screen.spent),
                  allowance: money(screen.allowance),
                })}
              </p>
              <p className={screen.soFar.minor < 0 ? 'over' : 'under'}>
                {screen.soFar.minor < 0
                  ? t('today.overPlan', {
                      amount: money({ ...screen.soFar, minor: -screen.soFar.minor }),
                    })
                  : t('today.underPlan', { amount: money(screen.soFar) })}
              </p>
            </div>
            <div className="today-bar" aria-hidden="true">
              <div style={{ width: `${String(Math.round(spentShare * 100))}%` }} />
            </div>
          </div>
        )}
      </section>

      {!online && screen.here && (
        <OfflineToday trip={id} day={screen.day} stop={screen.here.index} />
      )}
      {screen.day !== day && (
        <p className="today-stale" role="status">
          {t('today.stale', { day: weekday(screen.day, lang) })}
        </p>
      )}
      {/* A trip read only: the day's spends said, none added. */}
      {!locked && (
        <SpendForm trip={id} day={day} screen={screen} onAdded={reload} onQueued={recount} />
      )}
      <SpendList
        trip={id}
        day={day}
        screen={screen}
        waits={waits}
        onRemoved={reload}
        onDropped={recount}
      />

      <TodayWeather trip={id} day={screen.day} />

      {screen.reminders.length > 0 && (
        <section className="panel today-reminders" aria-labelledby="today-reminders">
          <h2 id="today-reminders">{t('docs.reminders')}</h2>
          <ul>
            {screen.reminders.map((crossing) => (
              <li key={`${crossing.country}-${String(crossing.entry)}`}>
                {t('docs.reminderLine', {
                  todo: crossing.rule
                    ? t(`docs.kind.${crossing.rule.kind}`, { form: crossing.rule.form })
                    : '',
                  country: countryName(crossing.country, lang),
                  due: t('docs.due', { day: weekday(crossing.due ?? crossing.border, lang) }),
                })}
              </li>
            ))}
          </ul>
          <Link to={`/trips/${id}/docs`}>{t('docs.remindersLink')}</Link>
        </section>
      )}
      {screen.next && (
        <section className="panel today-next" aria-labelledby="today-next">
          <h2 id="today-next">{t('today.next')}</h2>
          {screen.here && (
            <Link
              className="button button-small today-record"
              to={`/trips/${id}/stops/${String(screen.here.index)}/record`}
            >
              {t('record.link')}
            </Link>
          )}
          <p className="today-next-to">
            {t(`today.by.${screen.next.mode ?? 'none'}`, { to: screen.next.to })} ·{' '}
            {t('today.inDays', { count: screen.next.inDays })}
          </p>
          <p className="quiet">
            {screen.next.booked
              ? t('today.booked', { amount: money(screen.next.booked) })
              : t('today.notBooked', { range: formatRange(screen.next.estimate, lang) })}
          </p>
          {!screen.next.booked && (
            <Link className="button" to={`/trips/${id}/routes`}>
              {t('routes.open')}
            </Link>
          )}
        </section>
      )}
    </div>
  );
}

/** Logs a spend: its amount in the money paid, what for, a note and a photo of the bill. */
function SpendForm({
  trip,
  day,
  screen,
  onAdded,
  onQueued,
}: {
  trip: string;
  /** The day the spend is for: the one asked for, even when the screen is an older one kept. */
  day: string;
  screen: TodayScreen;
  onAdded: () => void;
  onQueued: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const photoInput = useRef<HTMLInputElement>(null);
  const [currency, setCurrency] = useState(screen.here?.currency ?? screen.currency);
  const [amount, setAmount] = useState<number | null>(null);
  const [category, setCategory] = useState<Category>('food');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  // A new key empties the amount field after a spend is kept.
  const [round, setRound] = useState(0);
  const [rating, setRating] = useState(false);

  const rate = screen.rates.find((r) => r.currency === currency);
  const estimate =
    amount !== null && rate && currency !== screen.currency
      ? formatMoney(
          {
            minor: convert(amount, currency, screen.currency, rate.perTrip),
            currency: screen.currency,
          },
          lang,
          true,
        )
      : null;

  const add = () => {
    if (amount === null) {
      setErrors({ amount: t('problem.required') });
      return;
    }
    setSending(true);
    setFailed(false);
    const input = {
      id: crypto.randomUUID(),
      day,
      category,
      note: note.trim(),
      amount,
      currency,
    };
    const cleared = () => {
      setErrors({});
      setNote('');
      setPhoto(null);
      setAmount(null);
      setRound((was) => was + 1);
    };
    addSpend(trip, input)
      .then((spend) => (photo ? uploadSpendBill(trip, spend.id, photo) : null))
      .then(
        () => {
          cleared();
          onAdded();
        },
        (error: unknown) => {
          if (error instanceof InvalidInput) {
            setErrors(
              Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
            );
          } else if (error instanceof TypeError) {
            // No connection: it waits on the phone, and goes when the connection is back.
            return keep(trip, input, photo).then(
              () => {
                cleared();
                onQueued();
              },
              () => {
                setFailed(true);
              },
            );
          } else {
            setFailed(true);
          }
        },
      )
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <section className="panel today-add" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>{t('today.add')}</h2>
      <AmountField
        key={`${currency}-${String(round)}`}
        label={t('today.amount')}
        value={amount}
        onChange={setAmount}
        currency={currency}
        currencies={screen.currencies.map((c) => c.code)}
        onCurrency={(code) => {
          setCurrency(code);
          setAmount(null);
        }}
        error={errors.amount}
      />
      {estimate && <p className="quiet">≈ {estimate}</p>}
      {currency !== screen.currency && (
        <p className="today-rate">
          {rate ? (
            <>
              {rateText(rate, screen.currency, lang)} ·{' '}
              {rate.source.kind === 'own'
                ? t('today.ownRate')
                : t(rate.source.kind === 'market' ? 'today.marketRate' : 'today.ecbRate', {
                    day: weekday(rate.source.day, lang),
                  })}
            </>
          ) : (
            <span className="field-error">{t('today.noRate')}</span>
          )}{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setRating(true);
            }}
          >
            {t('today.setRate')}
          </button>
        </p>
      )}
      {errors.currency && <p className="field-error">{errors.currency}</p>}
      <div className="today-categories" role="group" aria-label={t('today.category')}>
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            className={`chip chip-${c}`}
            aria-pressed={category === c}
            onClick={() => {
              setCategory(c);
            }}
          >
            {t(`today.categories.${c}`)}
          </button>
        ))}
      </div>
      <div className="field">
        <label className="field-label" htmlFor={`${ids}-note`}>
          {t('today.note')}
        </label>
        <input
          id={`${ids}-note`}
          className="text-input"
          type="text"
          value={note}
          maxLength={200}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </div>
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        tabIndex={-1}
        aria-hidden="true"
        data-testid="spend-photo"
        onChange={(event) => {
          setPhoto(event.target.files?.[0] ?? null);
        }}
      />
      <div className="today-actions">
        <button
          type="button"
          className="button"
          onClick={() => {
            photoInput.current?.click();
          }}
        >
          {t(photo ? 'today.photoAgain' : 'today.photo')}
        </button>
        <button
          type="button"
          className="button button-primary"
          disabled={sending}
          onClick={add}
        >
          {t('today.save')}
        </button>
      </div>
      {photo && <p className="today-photo">{t('today.photoAttached')}</p>}
      {failed && <p role="alert">{t('today.failed')}</p>}
      {rating && (
        <RateDialog
          trip={trip}
          currency={currency}
          tripCurrency={screen.currency}
          rate={rate}
          onClose={() => {
            setRating(false);
          }}
          onSaved={() => {
            setRating(false);
            onAdded();
          }}
        />
      )}
    </section>
  );
}

/** Sets the trip's own rate for a currency, or goes back to the ECB's. */
function RateDialog({
  trip,
  currency,
  tripCurrency,
  rate,
  onClose,
  onSaved,
}: {
  trip: string;
  currency: string;
  tripCurrency: string;
  rate: Rate | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const id = useId();
  const [text, setText] = useState(rate ? String(rate.perTrip / 1e9) : '');
  const [error, setError] = useState<string | null>(null);
  const done = (saved: Promise<null>) => {
    saved.then(onSaved, (problem: unknown) => {
      setError(t(problem instanceof InvalidInput ? 'today.rateWrong' : 'today.failed'));
    });
  };

  return (
    <Dialog
      open
      title={t('today.rateTitle', { currency: currencyName(currency, lang) })}
      onClose={onClose}
    >
      <div className="field">
        <label className="field-label" htmlFor={id}>
          {t('today.rateLabel', {
            trip: currencySymbol(tripCurrency, lang),
            currency: currencySymbol(currency, lang),
          })}
        </label>
        <input
          id={id}
          className="text-input"
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
      </div>
      {error && <p role="alert">{error}</p>}
      <div className="dialog-actions">
        {rate?.source.kind === 'own' && (
          <button
            type="button"
            className="button"
            onClick={() => {
              done(removeRate(trip, currency));
            }}
          >
            {t('today.rateEcb')}
          </button>
        )}
        <button
          type="button"
          className="button button-primary"
          onClick={() => {
            done(setRate(trip, currency, text.trim().replace(/[\s,]/g, '')));
          }}
        >
          {t('today.rateSave')}
        </button>
      </div>
    </Dialog>
  );
}

/** The day's spends, the latest first, each in its money and the trip's; then what waits. */
function SpendList({
  trip,
  day,
  screen,
  waits,
  onRemoved,
  onDropped,
}: {
  trip: string;
  day: string;
  screen: TodayScreen;
  waits: Waiting[];
  onRemoved: () => void;
  onDropped: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const what = (note: string, category: Category) => note || t(`today.categories.${category}`);
  const mine = waits.filter((w) => w.input.day === day);

  return (
    <section className="panel today-list" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>{t('today.spends')}</h2>
      {screen.spends.length === 0 && mine.length === 0 ? (
        <p className="quiet">{t('today.none')}</p>
      ) : (
        <ul>
          {mine.map((w) => (
            <li key={w.id} className="today-waiting">
              <span className={`dot dot-${w.input.category}`} aria-hidden="true" />
              <span className="today-what">{what(w.input.note, w.input.category)}</span>
              <span className="quiet">
                {w.refused === null
                  ? t('today.notSent')
                  : t('today.refused', { why: t(problemKey(w.refused)) })}
              </span>
              <span className="today-sum">
                {formatMoney({ minor: w.input.amount, currency: w.input.currency }, lang)}
              </span>
              {w.refused !== null && (
                <button
                  type="button"
                  className="link-button"
                  aria-label={t('today.forget', { what: what(w.input.note, w.input.category) })}
                  onClick={() => {
                    drop(w.id).then(onDropped, ignore);
                  }}
                >
                  ×
                </button>
              )}
            </li>
          ))}
          {screen.spends.map((spend) => (
            <li key={spend.id}>
              <span className={`dot dot-${spend.category}`} aria-hidden="true" />
              <span className="today-what">{what(spend.note, spend.category)}</span>
              {spend.photo && (
                <a href={spendBillUrl(trip, spend.id)} target="_blank" rel="noreferrer">
                  {t('today.bill')}
                </a>
              )}
              {spend.amount.currency !== spend.tripAmount.currency && (
                <span className="quiet">{formatMoney(spend.amount, lang)}</span>
              )}
              <span className="today-sum">{formatMoney(spend.tripAmount, lang, true)}</span>
              {!locked && (
                <button
                  type="button"
                  className="link-button"
                  aria-label={t('today.remove', { what: what(spend.note, spend.category) })}
                  onClick={() => {
                    removeSpend(trip, spend.id).then(onRemoved, ignore);
                  }}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
